import { ControlledTabs } from 'components/Form/ControlledTab';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import type {
    BimContractOtherStatusEnum,
    ChapterEnum,
    ConstructionTypeData,
    ContractOtherStatusEnum,
    ProjectMethodologyEnum,
} from 'gql/graphql';
import type { TypeItem } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { TabsIds } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import type { IUerContractOtherCostLimitSpanSelectionData } from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { GridItem, Loader, Text } from 'uikit';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { forwardRef, useMemo } from 'react';
import type { CSSProperties, ForwardedRef } from 'react';
import type { Control } from 'react-hook-form';
import type { UseFormClearErrors, UseFormSetValue } from 'react-hook-form/dist/types/form';

import type { IUpdateContractOtherCostCanEdit } from '../../pages/Project/Common/Contract/OtherEdit/hooks/useUerUpdateContractOtherCostCanEdit';
import { FormulaEditor } from '../FormulaEditor';

import { EditOtherCommon } from './EditOtherCommonNew';
import { EditOtherLimits } from './EditOtherLimitsNew';

import Styles from './EditOtherForm.module.css';

export const invisibleStyle = { visibility: 'hidden', position: 'absolute', overflow: 'hidden', maxHeight: 0 };

interface IEditOtherProps {
    activeTab: { label: string; id: string };
    handleTabChange: ({ value }: any) => void;
    tabItems: { id: string; label: string }[];
    control: Control;
    fieldsToCheck: { COMMON: string[]; PRICE: string[] };
    isDataFetching: boolean;
    selectedType: TypeItem;
    handleChangeType: (newType: any) => void;
    changeSelectedChapterOrSection: (value: any) => void;
    selectedChapterOrSection: any;
    currentConstructionTypeId: number;
    selectedConstructionType: Record<string, boolean>;
    handleChangeConstructionType: (id: number, state: boolean) => void;
    constructionTypes?: ConstructionTypeData[];
    otherStatus?: BimContractOtherStatusEnum | ContractOtherStatusEnum;
    methodology: ProjectMethodologyEnum;
    getObjectSourceQuery?: TypedDocumentNode<any, any>;
    costRounding: number;
    clearErrors?: UseFormClearErrors<any>;
    setValue: UseFormSetValue<any>;
    spanSelectionData?: IUerContractOtherCostLimitSpanSelectionData;
    otherId: number;
    chapter?: ChapterEnum | null;
    isGlobalOtherCost?: boolean;
    name?: string;
    calculatingTypeValue?: number;
    canEditConfig?: IUpdateContractOtherCostCanEdit;
}

export const EditOtherFormNew = forwardRef<HTMLFormElement, IEditOtherProps>(
    (props, ref: ForwardedRef<HTMLFormElement>) => {
        const {
            activeTab,
            handleTabChange,
            tabItems,
            control,
            fieldsToCheck,
            isDataFetching,
            selectedType,
            handleChangeType,
            changeSelectedChapterOrSection,
            selectedChapterOrSection,
            currentConstructionTypeId,
            selectedConstructionType,
            handleChangeConstructionType,
            constructionTypes,
            otherStatus,
            methodology,
            getObjectSourceQuery,
            costRounding,
            clearErrors,
            setValue,
            spanSelectionData,
            otherId,
            chapter,
            isGlobalOtherCost,
            name,
            calculatingTypeValue,
            canEditConfig,
        } = props;

        const commonEditConfig = useMemo(() => canEditConfig?.[TabsIds.COMMON], [canEditConfig]);

        const limitEditConfig = useMemo(() => canEditConfig?.[TabsIds.PRICE], [canEditConfig]);

        const formulaEditConfig = useMemo(() => canEditConfig?.[TabsIds.FORMULA], [canEditConfig]);

        const commonTabStyle = useMemo(
            () => (activeTab.id === TabsIds.COMMON ? undefined : invisibleStyle),
            [activeTab.id],
        );

        const limitsTabStyle = useMemo(
            () => (activeTab.id === TabsIds.PRICE ? undefined : invisibleStyle),
            [activeTab.id],
        );

        const formulaTabStyle = useMemo(
            () => (activeTab.id === TabsIds.FORMULA ? undefined : invisibleStyle),
            [activeTab.id],
        );

        return (
            <>
                {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}

                <div className={Styles.innerBlock}>
                    <ControlledTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        items={tabItems}
                        size="s"
                        control={control}
                        fieldsToCheck={fieldsToCheck}
                    />
                    {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}
                    {!isDataFetching && (
                        <form ref={ref}>
                            <div className={Styles.tabsContent}>
                                <div style={commonTabStyle as CSSProperties}>
                                    <EditOtherCommon
                                        control={control}
                                        isGlobal={isGlobalOtherCost}
                                        globalOtherName={name}
                                        otherStatus={otherStatus}
                                        chapter={chapter}
                                        selectedType={selectedType}
                                        changeSelectedTypeHandler={handleChangeType}
                                        changeSelectedChapterOrSection={changeSelectedChapterOrSection}
                                        selectedChapterOrSection={selectedChapterOrSection}
                                        isTooltipHidden={!!commonTabStyle}
                                        methodology={methodology}
                                        calculatingTypeValue={calculatingTypeValue}
                                        otherId={otherId}
                                        editConfig={commonEditConfig}
                                    />
                                </div>
                                <div style={limitsTabStyle as CSSProperties}>
                                    <EditOtherLimits
                                        control={control}
                                        currentConstructionTypeId={currentConstructionTypeId}
                                        selectedConstructionType={selectedConstructionType}
                                        changeSelectedConstructionTypeHandler={handleChangeConstructionType}
                                        constructionTypes={constructionTypes}
                                        isTooltipHidden={!!limitsTabStyle}
                                        costRounding={costRounding}
                                        clearErrors={clearErrors}
                                        setValue={setValue}
                                        spanSelectionData={spanSelectionData}
                                        editConfig={limitEditConfig}
                                    />
                                </div>

                                <GridItem style={formulaTabStyle as CSSProperties}>
                                    {selectedType?.id === OtherCostCalculationTypeEnum?.Percent &&
                                    getObjectSourceQuery ? (
                                        <FormulaEditor
                                            name="formula"
                                            control={control}
                                            getObjectSourceQuery={getObjectSourceQuery}
                                            isDisabled={!formulaEditConfig}
                                            tooltipTitle="Формула расчета"
                                            rules={{
                                                required: !!formulaEditConfig && 'Обязательное поле',
                                            }}
                                            isTooltipHidden={!!formulaTabStyle}
                                            isRequired={!!formulaEditConfig}
                                            isReadOnly={!formulaEditConfig}
                                        />
                                    ) : (
                                        <Text view="secondary" as="span" className={Styles.formulaUnavailableText}>
                                            Для способа расчета &quot;Расчет&quot; ввод формул недоступен
                                        </Text>
                                    )}
                                </GridItem>
                            </div>
                        </form>
                    )}
                </div>
            </>
        );
    },
);

EditOtherFormNew.displayName = 'EditOtherForm';
